import debounce from 'lodash/debounce';
import { HTML_TAG_NAME_MAIN } from '../constants/system/html-tag-names';

export default class ScreenService {
  private static BEHAVIOR_SMOOTH: ScrollBehavior = 'smooth';
  private static HEADER_HEIGHT: number = 64;
  private static SCROLL_RATE_FAST: number = 0.5;
  private static SCROLL_RATE_DEFAULT: number = 0.9;
  private static DELAY_DEFAULT: number = 500;

  public static getSelection(): Selection | null {
    if (typeof window !== 'undefined' && window.getSelection()) {
      return window.getSelection();
    }

    return null;
  }

  public static checkNoSelection(): boolean {
    const selection: Selection | null = this.getSelection();

    return !selection || (!!selection && !selection.toString());
  }

  public static resetScrollPosition(element?: HTMLElement): void {
    if (typeof window !== 'undefined') {
      if (element) {
        element.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
    }
  }

  public static scrollToElement(elementId?: string, containerId?: string, topOffset?: number): void {
    if (typeof window !== 'undefined' && elementId) {
      const element: HTMLElement | null = window.document.getElementById(elementId);
      const mainContainer: HTMLElement | null = containerId ? window.document.getElementById(containerId)
        : window.document.getElementsByTagName(HTML_TAG_NAME_MAIN)[0] as HTMLElement;

      if (element && mainContainer) {
        mainContainer.scrollTo({
          top: element.offsetTop - this.HEADER_HEIGHT - (topOffset || 0),
          left: element.offsetLeft,
          behavior: this.BEHAVIOR_SMOOTH,
        });
      }
    }
  }

  public static downloadFile(fileLink: string): void {
    if (fileLink && typeof window !== 'undefined') {
      window.open(fileLink);
    }
  }

  public static infiniteScroll(
    containerTopOffset: number,
    containerHeight: number,
    itemListHeight: number,
    onScroll?: () => void,
    withWindowOffsetIncluded?: boolean,
    withFastScrollRate?: boolean,
  ): void {
    if (typeof window !== 'undefined') {
      const mainElements: HTMLCollectionOf<HTMLElement> = this.getElementsByTagName(HTML_TAG_NAME_MAIN);
      const mainElement: HTMLElement = mainElements[0] as HTMLElement;

      const containerOffset: number = withWindowOffsetIncluded ? mainElement.scrollTop : containerHeight;
      const scrollRate: number = withFastScrollRate ? this.SCROLL_RATE_FAST : this.SCROLL_RATE_DEFAULT;

      if (onScroll && (itemListHeight > window.innerHeight)
        && (containerTopOffset + containerOffset > itemListHeight * scrollRate)) {
        onScroll();
      }
    }
  }

  public static withDelay(onScroll: ScrollEventHandler, delay?: number): ScrollEventHandler {
    return debounce(onScroll, delay || this.DELAY_DEFAULT);
  }

  public static createElement(elementTagName: string): HTMLElement | null {
    if (typeof window !== 'undefined') {
      return window.document.createElement(elementTagName);
    }

    return null;
  }

  public static getElementById(elementId: string): HTMLElement | null {
    return typeof window !== 'undefined' ? window.document.getElementById(elementId) : null;
  }

  public static getElementByClassName(elementClassName: string): HTMLElement | null {
    return typeof window !== 'undefined' ? window.document.querySelector(`.${elementClassName}`) : null;
  }

  public static getElementsByClassName(elementClassName: string): HTMLCollectionOf<HTMLElement> {
    return (typeof window !== 'undefined' ? window.document.getElementsByClassName(elementClassName)
      : new HTMLCollection()) as HTMLCollectionOf<HTMLElement>;
  }

  public static getElementsByTagName(tagName: string): HTMLCollectionOf<HTMLElement> {
    return (typeof window !== 'undefined' ? window.document.getElementsByTagName(tagName)
      : new HTMLCollection()) as HTMLCollectionOf<HTMLElement>;
  }

  public static getChildElementsByTagName(element: HTMLElement, tagName: string): NodeListOf<HTMLElement> {
    return element ? element.querySelectorAll(tagName) : new NodeList() as NodeListOf<HTMLElement>;
  }

  public static getActiveElement(): HTMLElement | null {
    return typeof window !== 'undefined' ? window.document.activeElement as HTMLElement || null : null;
  }

  public static removeHeadElement(element: HTMLElement): void {
    if (typeof window !== 'undefined') {
      window.document.head.removeChild(element);
    }
  }

  public static removeAllElementsByClassName(elementClassName: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.document.querySelectorAll(`.${elementClassName}`).forEach((element: Element): void => {
      element.remove();
    });
  }

  public static addEventListener(eventName: string, eventListener?: ReactEventListener): void {
    if (typeof window !== 'undefined' && eventListener) {
      window.addEventListener(eventName, eventListener);
    }
  }

  public static removeEventListener(eventName: string, eventListener?: ReactEventListener): void {
    if (typeof window !== 'undefined' && eventListener) {
      window.removeEventListener(eventName, eventListener);
    }
  }

  public static openLink(link: string): void {
    if (link && typeof window !== 'undefined') {
      window.location.href = link;
    }
  }

  public static addHtmlScript(scriptLink: string, isAsync: boolean = false): HTMLScriptElement | null {
    if (typeof window !== 'undefined' && window.getSelection() && scriptLink) {
      const htmlElementScript: HTMLScriptElement = window.document.createElement('script');

      htmlElementScript.src = scriptLink;
      htmlElementScript.async = isAsync;

      window.document.head.appendChild(htmlElementScript);

      return htmlElementScript;
    }

    return null;
  }

  public static async copyToClipboard(text: string): Promise<void> {
    // The access to object window.navigator.clipboard requires HTTPS connection as well
    if (typeof window !== 'undefined' && 'clipboard' in window.navigator) {
      await window.navigator.clipboard.writeText(text);
    }
  }

  public static checkIsBrowser(): boolean {
    return typeof window !== 'undefined';
  }
}
